import { useRouter } from 'next/router'
import { useRecoilValue } from 'recoil'
import styled from 'styled-components'
import InfiniteScroll from 'react-infinite-scroll-component'

import { FaInstagram } from "react-icons/fa";
import { FiGrid } from "react-icons/fi";

import { extractInstagramUsername } from '@/util/urlHelpers'

import { subDataState } from '@/util/states'
import { userInfoState } from '@/util/states'

import { useGetUser } from 'src/hooks/useGetUser'
import { useGetPosts } from 'src/hooks/posts/useGetPosts'
import { useGetUserPostCount } from 'src/hooks/users/useGetUserPostCount';
import { useScreenshotAttempt } from 'src/hooks/useScreenshotAttempt'

import { ScreenshotModal } from '@/components/shared/ScreenshotModal'
import Spinner from 'src/components/Spinner'
import { PostContentDisplay } from '@/components/Discover/PostContentDisplay';
import TipButton from 'src/components/TipButton';
import ReportButton from 'src/components/ReportButton';
import ProfileLiveLink from '@/components/Live/ProfileLiveLink';

const SingleUser = () => {
  const router = useRouter()
  const userData = useRecoilValue(userInfoState)
  const subData = useRecoilValue(subDataState)
  const { username, tip } = router.query
  const {
    user: {
      id,
      avatar,
      description,
      headline,
      url,
    },
    isLoading
  } = useGetUser({ username })
  const { data: { postCount } } = useGetUserPostCount({ userId: id })
  const { posts, isLoading: fetchingPosts, hasMore, loadMore } = useGetPosts({
    userId: id,
    status: "live",
    orderBy: 'approvedAt',
    limit: 2,
    enablePagination: true,
    enabled: !!id
  })
  const initialLoading = !posts?.length && fetchingPosts;
  const { screenshotAttempt, setScreenshotAttempt } = useScreenshotAttempt()

  if (subData === 'pending' || isLoading || initialLoading) return <Spinner />
  if (!id) return null

  const profile = {
    avatar,
    authorUsername: username,
    headline,
  }

  return (
    <div className="w-full font-soehne-kraftig">
      {screenshotAttempt && (
        <ScreenshotModal onClose={() => setScreenshotAttempt(false)} />
      )}

      <PageLayout>
        <Sidebar>
          <SidebarInner>
            <ProfileIcon src={avatar} />
            <h1 className="uppercase tracking-wide text-center text-md mb-2 text-bold">
              @{username}
            </h1>

            <ProfileLiveLink username={username} />

            <UserDetails>
              {headline && <HeadlineText>{headline}</HeadlineText>}
              {description && <DescriptionText>{description}</DescriptionText>}

              <PostCount>
                <FiGrid style={{ marginRight: '6px' }} />
                {postCount || 0} {postCount === 1 ? 'post' : 'posts'}
              </PostCount>

              {!(userData && userData !== 'pending' && userData.uid === id) && (
                <TipButtonWrapper>
                  <TipButton
                    creatorId={id}
                    creatorUsername={username}
                    defaultAmount={5}
                    buttonText="Tip Creator"
                    autoOpen={tip === 'true'}
                  />
                </TipButtonWrapper>
              )}

              {extractInstagramUsername(url) && (
                <InstagramLink>
                  <FaInstagram />
                  <UrlText href={url} target="_blank" rel="noopener noreferrer">
                    {extractInstagramUsername(url)}
                  </UrlText>
                </InstagramLink>
              )}

              {!(userData && userData !== 'pending' && userData.uid === id) && (
                <ReportWrapper>
                  <ReportButton targetType="profile" targetId={id} targetLabel={`@${username}`} />
                </ReportWrapper>
              )}
            </UserDetails>
          </SidebarInner>
        </Sidebar>

        <FeedColumn>
          <Posts
            posts={posts}
            unlockedDiscoveries={subData.unlockedDiscoveries}
            loadMore={loadMore}
            hasMore={hasMore}
            userData={userData}
            profile={profile}
          />
        </FeedColumn>
      </PageLayout>
    </div>
  )
}

const Posts = ({ posts, unlockedDiscoveries, loadMore, hasMore, userData, profile }) => {
  if (!posts) return null;

  return (
    <InfiniteScroll
      dataLength={posts.length}
      next={loadMore}
      hasMore={hasMore}
      loader={<Spinner />}
    >
      <PostContainer>
        {posts.map((post) => (
          <Post
            key={post.id}
            post={post}
            unlockedDiscoveries={unlockedDiscoveries}
            userData={userData}
            profile={profile}
          />
        ))}
      </PostContainer>
    </InfiniteScroll>
  )
}

const Post = ({ post, unlockedDiscoveries, userData, profile }) => {
  const unlockedPost = !!unlockedDiscoveries?.includes(post.id)

  return (
    <PostContentDisplay
      post={post}
      userData={userData}
      profile={profile}
      locked={!unlockedPost}
      showRelatedSections={false}
      wide
      hideProfile
    />
  )
}

const PageLayout = styled.div`
  display: flex;
  flex-direction: row;
  min-height: 100vh;

  @media (max-width: 800px) {
    flex-direction: column;
  }
`

const Sidebar = styled.aside`
  width: 300px;
  flex-shrink: 0;
  border-right: 1px solid #e5e5e5;

  @media (max-width: 800px) {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #e5e5e5;
  }
`

const SidebarInner = styled.div`
  position: sticky;
  top: 0;
  padding: 56px 24px 32px;

  @media (max-width: 800px) {
    position: static;
    padding: 20px 16px;
  }
`

const FeedColumn = styled.div`
  flex: 1;
  min-width: 0;
  padding-left: 24px;

  @media (max-width: 800px) {
    padding-left: 0;
  }
`

const PostCount = styled.div`
  font-size: 14px;
  color: #555;
  display: flex;
  align-items: center;
  margin-top: 4px;
`

const TipButtonWrapper = styled.div`
  margin-top: 12px;
  margin-bottom: 8px;
`

const ReportWrapper = styled.div`
  margin-top: 16px;
`

const InstagramLink = styled.div`
  display: flex;
  align-items: center;
`

const UrlText = styled.a`
  display: inline-block;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
  margin-left: 6px;
  color: #000;
`

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
`

const HeadlineText = styled.span.attrs({ className: 'font-soehne-kraftig' })`
  font-size: 16px;
  text-align: center;
`

const DescriptionText = styled.span.attrs({ className: 'font-soehne-leight' })`
  font-size: 14px;
  text-align: center;
  color: #555;
`

const ProfileIcon = styled.img`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: block;
  margin: 12px auto;
  object-fit: cover;
  object-position: top;
`

const PostContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

export default SingleUser
